// SPDX-License-Identifier: LicenseRef-Proprietary

import type { PubmedPaperSummary } from '../../../../../services/pubmedService';
import type { AbstractTriageBrief } from '../../../lib/models';
import type { PaperAnalysisStatus } from './paperStatus';
import { determinePaperStatus } from './paperStatus';
import type { DetailedTriageError } from './detailedTriageError';
import { createDetailedTriageError } from './detailedTriageError';

/**
 * Paper with enhanced status information for categorization
 */
export interface PaperWithStatus extends PubmedPaperSummary {
  analysisStatus: PaperAnalysisStatus;
  aiBrief?: AbstractTriageBrief;
  triageError?: { code: string; message: string };
  detailedError?: DetailedTriageError;
  lastAnalyzed?: string; // ISO timestamp
  selectable: boolean;
}

/**
 * Categorized analysis results for organized display
 */
export interface CategorizedResults {
  highlyRelevant: PaperWithStatus[];    // Multiple relevant parameters
  somewhatRelevant: PaperWithStatus[];  // Few relevant parameters
  notRelevant: PaperWithStatus[];       // No relevant parameters (but analyzed)
  analysisFailed: PaperWithStatus[];    // Technical failures
  notAnalyzed: PaperWithStatus[];       // Not yet processed
}

/**
 * Filter options for result display
 */
export type ResultFilter = 'all' | 'relevant' | 'not_relevant' | 'failed' | 'not_analyzed';

/**
 * Category display information
 */
export interface CategoryInfo {
  key: keyof CategorizedResults;
  label: string;
  description: string;
  icon: string;
  color: 'success' | 'warning' | 'neutral' | 'error' | 'default';
}

/**
 * Category configuration for display
 */
export const CATEGORY_CONFIG: Record<keyof CategorizedResults, CategoryInfo> = {
  highlyRelevant: {
    key: 'highlyRelevant',
    label: 'Highly Relevant',
    description: 'Papers with multiple relevant parameters for your analysis',
    icon: '',
    color: 'success'
  },
  somewhatRelevant: {
    key: 'somewhatRelevant',
    label: 'Somewhat Relevant',
    description: 'Papers with some relevant parameters or partial matches',
    icon: '',
    color: 'warning'
  },
  notRelevant: {
    key: 'notRelevant',
    label: 'Not Relevant',
    description: 'Papers analyzed but no relevant parameters found',
    icon: '',
    color: 'neutral'
  },
  analysisFailed: {
    key: 'analysisFailed',
    label: 'Analysis Failed',
    description: 'Papers that could not be processed due to technical issues',
    icon: '',
    color: 'error'
  },
  notAnalyzed: {
    key: 'notAnalyzed',
    label: 'Not Analyzed',
    description: 'Papers not yet processed by AI analysis',
    icon: '',
    color: 'default'
  }
};

/**
 * Convert papers to categorized results based on analysis status
 */
export function categorizePapers(
  papers: PubmedPaperSummary[],
  triageBriefs: Record<string, AbstractTriageBrief>,
  triageErrors: Record<string, { code: string; message: string }>
): CategorizedResults {
  const categorized: CategorizedResults = {
    highlyRelevant: [],
    somewhatRelevant: [],
    notRelevant: [],
    analysisFailed: [],
    notAnalyzed: []
  };

  papers.forEach(paper => {
    const brief = triageBriefs[paper.pmid];
    const error = triageErrors[paper.pmid];
    const effectiveError = brief ? undefined : error;
    const analysisStatus = determinePaperStatus(brief, effectiveError, false);

    // Create detailed error if basic error exists
    const detailedError = effectiveError ? createDetailedTriageError(paper.pmid, effectiveError) : undefined;

    const paperWithStatus: PaperWithStatus = {
      ...paper,
      analysisStatus,
      aiBrief: brief,
      triageError: effectiveError,
      detailedError,
      lastAnalyzed: brief ? new Date().toISOString() : undefined,
      selectable: true
    };

    switch (analysisStatus) {
      case 'analyzed_relevant': {
        // Count relevant parameters to determine relevance level
        const potentialCount = brief?.potentialInputSources?.filter(s => s !== 'none').length || 0;
        const retrievableCount = brief?.retrievable_inputs?.length || 0;
        const totalRelevantParams = potentialCount + retrievableCount;

        if (totalRelevantParams >= 3) {
          categorized.highlyRelevant.push(paperWithStatus);
        } else {
          categorized.somewhatRelevant.push(paperWithStatus);
        }
        break;
      }
      case 'analyzed_empty':
        categorized.notRelevant.push(paperWithStatus);
        break;
      case 'analysis_failed':
      case 'analysis_error':
        categorized.analysisFailed.push(paperWithStatus);
        break;
      case 'analyzing':
      case 'not_analyzed':
      default:
        categorized.notAnalyzed.push(paperWithStatus);
        break;
    }
  });

  return categorized;
}

/**
 * Filter categorized results based on selected filter
 */
export function filterCategorizedResults(
  categorized: CategorizedResults,
  filter: ResultFilter
): CategorizedResults {
  switch (filter) {
    case 'relevant':
      return {
        highlyRelevant: categorized.highlyRelevant,
        somewhatRelevant: categorized.somewhatRelevant,
        notRelevant: [],
        analysisFailed: [],
        notAnalyzed: []
      };
    case 'not_relevant':
      return {
        highlyRelevant: [],
        somewhatRelevant: [],
        notRelevant: categorized.notRelevant,
        analysisFailed: [],
        notAnalyzed: []
      };
    case 'failed':
      return {
        highlyRelevant: [],
        somewhatRelevant: [],
        notRelevant: [],
        analysisFailed: categorized.analysisFailed,
        notAnalyzed: []
      };
    case 'not_analyzed':
      return {
        highlyRelevant: [],
        somewhatRelevant: [],
        notRelevant: [],
        analysisFailed: [],
        notAnalyzed: categorized.notAnalyzed
      };
    case 'all':
    default:
      return categorized;
  }
}

/**
 * Get total count of papers in categorized results
 */
export function getCategorizedResultsCount(categorized: CategorizedResults): number {
  return Object.values(categorized).reduce((total, papers) => total + papers.length, 0);
}

/**
 * Get count of papers for a specific category
 */
export function getCategoryCount(categorized: CategorizedResults, category: keyof CategorizedResults): number {
  return categorized[category].length;
}

/**
 * Check if a category has any papers
 */
export function isCategoryEmpty(categorized: CategorizedResults, category: keyof CategorizedResults): boolean {
  return categorized[category].length === 0;
}

/**
 * Get relevance explanation for a paper
 */
export function getRelevanceExplanation(paper: PaperWithStatus): string {
  if (!paper.aiBrief) {
    return 'Paper has not been analyzed yet.';
  }

  const potentialSources = paper.aiBrief.potentialInputSources?.filter(s => s !== 'none') || [];
  const retrievableInputs = paper.aiBrief.retrievable_inputs || [];

  if (potentialSources.length === 0 && retrievableInputs.length === 0) {
    return 'No relevant parameters were identified in this paper.';
  }

  const explanations: string[] = [];
  
  if (potentialSources.length > 0) {
    explanations.push(`Found ${potentialSources.length} potential input parameter${potentialSources.length !== 1 ? 's' : ''}`);
  }
  
  if (retrievableInputs.length > 0) {
    explanations.push(`identified ${retrievableInputs.length} retrievable input${retrievableInputs.length !== 1 ? 's' : ''}`);
  }

  return explanations.join(' and ') + '.';
}
